/*
 * Copyright (c) 2026 Dobler Data Solutions, LLC
 * Fabric Control Plane - NSG Summary Dashboard
 * Feature #140: Network Topology & Security Dashboard - US4
 * Constitution: v1.12.0
 * Conformed to Spec 197 design blueprint
 */

import React, { useState, useMemo } from 'react';
import {
  Shield, ArrowDownToLine, ArrowUpFromLine, Globe2,
  RefreshCw, Search, AlertTriangle,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useNetworkSummary, useNsgs, useNsgDetail } from '../../services/operations/networkService';
import { useDebounce } from '../../hooks/useDebounce';
import { ExportButton } from '../../components/operations';
import {
  PageLayout,
  Card,
  DataTable,
  Button,
  Input,
  Badge,
  StatCard,
  NotCollected,
  EmptyState,
  ErrorState,
  LoadingState,
  Modal,
  Extended,
  QuickGlance,
} from '../../components/ui';
import type { DataTableColumn } from '../../components/ui';
import type { NSG } from '../../types/network';
import { NoAccessBadge, WorkerVisibilityNotice } from '../../components/collection/WorkerVisibility';

// ============================================================================
// NSG Detail Modal (T036-T038)
// ============================================================================

const NsgDetailModal: React.FC<{
  nsgId: string;
  onClose: () => void;
}> = ({ nsgId, onClose }) => {
  const { data, isLoading } = useNsgDetail(nsgId);
  const [activeTab, setActiveTab] = useState<'summary' | 'subnets' | 'nics'>('summary');

  const tabs = [
    { key: 'summary' as const, label: 'Summary' },
    { key: 'subnets' as const, label: 'Associated Subnets', count: data?.associatedSubnets.length },
    { key: 'nics' as const, label: 'Associated NICs', count: data?.nsg?.associated_nic_count },
  ];

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="lg"
      title={
        <div>
          <div>{data?.nsg?.nsg_name ?? 'Loading...'}</div>
          {data?.nsg && (
            <div className="text-sm font-normal text-[var(--color-text-secondary)]">{data.nsg.resource_group}</div>
          )}
        </div>
      }
      footer={
        <Button variant="secondary" size="sm" onClick={onClose}>
          Close
        </Button>
      }
    >
      {/* Tabs */}
      <div className="mb-4 flex gap-1 border-b border-[var(--color-border)]">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={clsx(
              '-mb-px flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium transition-colors',
              activeTab === tab.key
                ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
                : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
            )}
          >
            {tab.label}
            {tab.count !== undefined && (
              <Badge variant="secondary" size="xs">{tab.count}</Badge>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {isLoading ? (
        <LoadingState rows={5} />
      ) : !data?.nsg ? (
        <ErrorState title="NSG not found" message="The requested NSG could not be loaded." />
      ) : (
        <>
          {/* T037: Summary tab */}
          {activeTab === 'summary' && (
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {[
                { label: 'Custom Inbound Rules', value: countText(data.nsg.custom_inbound_rules) },
                { label: 'Custom Outbound Rules', value: countText(data.nsg.custom_outbound_rules) },
                { label: 'Default Inbound Rules', value: countText(data.nsg.default_inbound_rules) },
                { label: 'Default Outbound Rules', value: countText(data.nsg.default_outbound_rules) },
                { label: 'Associated Subnets', value: countText(data.nsg.associated_subnet_count) },
                { label: 'Associated NICs', value: countText(data.nsg.associated_nic_count) },
                { label: 'Provisioning State', value: data.nsg.provisioning_state },
                { label: 'Location', value: data.nsg.location },
                { label: 'Resource Group', value: data.nsg.resource_group },
                { label: 'Snapshot Date', value: data.nsg.snapshot_date },
                { label: 'Azure Resource ID', value: data.nsg.azure_resource_id },
              ].map((p) => (
                <div key={p.label} className={clsx('py-2', p.label === 'Azure Resource ID' && 'sm:col-span-2')}>
                  <dt className="text-xs font-medium uppercase tracking-wider text-[var(--color-text-secondary)]">{p.label}</dt>
                  <dd className={clsx('mt-1 text-sm text-[var(--color-text-primary)]', p.label === 'Azure Resource ID' && 'break-all font-mono')}>{p.value}</dd>
                </div>
              ))}
            </dl>
          )}

          {/* T038: Associated Subnets tab */}
          {activeTab === 'subnets' && (
            data.associatedSubnets.length === 0 ? (
              <EmptyState title="No associated subnets" message="No associated subnets found." />
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-[var(--color-border)]">
                      {['Subnet', 'VNet', 'Address Prefix'].map((h) => (
                        <th key={h} className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-[var(--color-text-secondary)]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.associatedSubnets.map((s, i) => (
                      <tr key={i} className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-subtle)]">
                        <td className="px-3 py-2 font-medium text-[var(--color-text-primary)]">{s.subnet_name}</td>
                        <td className="px-3 py-2 text-[var(--color-text-secondary)]">{s.vnet_name}</td>
                        <td className="px-3 py-2 font-mono text-[var(--color-text-secondary)]">{s.address_prefix}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {/* NICs tab — data from nsg_snapshots count only */}
          {activeTab === 'nics' && (
            <p className="py-4 text-sm text-[var(--color-text-secondary)]">
              {data.nsg.associated_nic_count == null
                ? 'The NIC count is not collected for this NSG.'
                : data.nsg.associated_nic_count > 0
                  ? `${data.nsg.associated_nic_count} NIC(s) associated with this NSG. Individual NIC details are not collected in the current snapshot.`
                  : 'No associated NICs found.'}
            </p>
          )}
        </>
      )}
    </Modal>
  );
};

// ============================================================================
// Main Page Component (T032-T039)
// ============================================================================

export /** A count, or 'Not collected' when no snapshot carries it (T159 item 22; it read 0 before). */
function countText(n: number | null | undefined): string {
  return n != null ? String(n) : 'Not collected';
}

/** The sum over the NSGs that carry the figure, and how many those are. */
function countedTotal(nsgs: NSG[], pick: (n: NSG) => number | null): { sum: number; counted: number } {
  return nsgs.reduce((acc, n) => {
    const v = pick(n);
    return v == null ? acc : { sum: acc.sum + v, counted: acc.counted + 1 };
  }, { sum: 0, counted: 0 });
}

/** A total tile that names its population when not every NSG is counted, and reads not collected when none is. */
function CountedStatCard({ label, total, of, icon, tooltip }: { label: string; total: { sum: number; counted: number }; of: number; icon: React.ReactNode; tooltip?: React.ReactNode }) {
  return (
    <StatCard
      label={label}
      value={total.counted === 0 && of > 0 ? <NotCollected reason="No NSG's latest snapshot carries this count." /> : total.sum}
      subtext={total.counted > 0 && total.counted < of ? `Counted on ${total.counted} of ${of} NSGs` : undefined}
      icon={icon}
      tooltip={tooltip}
    />
  );
}

/** Spec 292 T033 (audit F11): the rule tiles count an NSG's CUSTOM rules; Azure's default rules are not counted. */
const CUSTOM_RULES_NOTE = "Custom rules only — Azure's default rules on every NSG are not counted.";

const NsgSummaryPage: React.FC = () => {
  const [search, setSearch] = useState<string>('');
  const [selectedNsgId, setSelectedNsgId] = useState<string | null>(null);

  const debouncedSearch = useDebounce(search, 300);

  const filters = useMemo(() => {
    const f: Record<string, unknown> = {};
    if (debouncedSearch) f.search = debouncedSearch;
    return f;
  }, [debouncedSearch]);

  const summaryQuery = useNetworkSummary();
  const nsgsQuery = useNsgs(filters);
  const summary = summaryQuery.data;
  const nsgs = nsgsQuery.data ?? [];

  // Computed KPI values from NSG data. Spec 286 T159 item 22: an NSG whose latest snapshot carries no count adds nothing
  // (null read as 0 before, so the tiles showed 0 for an estate nobody had counted); each total says how many it counts.
  const totalInbound = useMemo(() => countedTotal(nsgs, (n) => n.custom_inbound_rules), [nsgs]);
  const totalOutbound = useMemo(() => countedTotal(nsgs, (n) => n.custom_outbound_rules), [nsgs]);
  const totalAssociatedSubnets = useMemo(() => countedTotal(nsgs, (n) => n.associated_subnet_count), [nsgs]);

  const isLoading = summaryQuery.isLoading && nsgsQuery.isLoading;

  const refetchAll = () => {
    summaryQuery.refetch();
    nsgsQuery.refetch();
  };

  const columns: DataTableColumn<NSG>[] = [
    {
      key: 'nsg_name',
      header: 'NSG Name',
      width: '22%',
      render: (nsg) => (
        <span className="inline-flex flex-wrap items-center gap-2">
          <span className="font-medium text-[var(--color-accent)]">{nsg.nsg_name}</span>
          {nsg.no_access_since && <NoAccessBadge since={nsg.no_access_since} />}
        </span>
      ),
    },
    { key: 'resource_group', header: 'Resource Group', width: '20%' },
    { key: 'location', header: 'Location', width: '12%' },
    { key: 'custom_inbound_rules', header: 'Custom inbound rules', width: '11%', align: 'right', render: (nsg) => nsg.custom_inbound_rules ?? '—' },
    { key: 'custom_outbound_rules', header: 'Custom outbound rules', width: '11%', align: 'right', render: (nsg) => nsg.custom_outbound_rules ?? '—' },
    { key: 'associated_subnet_count', header: 'Subnets', width: '8%', align: 'right', render: (nsg) => nsg.associated_subnet_count ?? '—' },
    { key: 'associated_nic_count', header: 'NICs', width: '8%', align: 'right', render: (nsg) => nsg.associated_nic_count ?? '—' },
    {
      key: 'complexity',
      header: '',
      width: '8%',
      render: (nsg) =>
        (nsg.custom_inbound_rules ?? 0) > 10 ? (
          <Badge variant="warning" leftIcon={<AlertTriangle className="h-3 w-3" />}>Complex</Badge>
        ) : null,
    },
  ];

  return (
    <PageLayout
      helpId="auto.nsg-summary"
      title="NSG Summary"
      description="Network Security Group inventory and rule count aggregation"
      icon={<Shield className="h-6 w-6" />}
      actions={
        <>
          <Button variant="outline" size="sm" leftIcon={<RefreshCw className="h-4 w-4" />} onClick={refetchAll}>
            Refresh
          </Button>
          <ExportButton
            data={nsgs}
            filename="nsg-summary"
            columns={[
              { key: 'nsg_name', label: 'NSG Name' },
              { key: 'resource_group', label: 'Resource Group' },
              { key: 'location', label: 'Location' },
              { key: 'custom_inbound_rules', label: 'Custom inbound rules' },
              { key: 'custom_outbound_rules', label: 'Custom outbound rules' },
              { key: 'associated_subnet_count', label: 'Subnets' },
              { key: 'associated_nic_count', label: 'NICs' },
              { key: 'provisioning_state', label: 'State' },
            ]}
          />
        </>
      }
    >
      {isLoading ? (
        <LoadingState rows={8} />
      ) : (
        <>
          {summary && (
            <WorkerVisibilityNotice
              since={summary.noAccessSince}
              counts={[{ noun: 'NSGs', noAccess: summary.noAccessNsgs, total: summary.totalNsgs }]}
            />
          )}
          {/* KPI tiles (T033) */}
          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <StatCard label="NSGs" value={summary?.totalNsgs ?? nsgs.length} icon={<Shield className="h-5 w-5" />} />
            <CountedStatCard label="Custom inbound rules" total={totalInbound} of={nsgs.length} icon={<ArrowDownToLine className="h-5 w-5" />} tooltip={CUSTOM_RULES_NOTE} />
            <CountedStatCard label="Custom outbound rules" total={totalOutbound} of={nsgs.length} icon={<ArrowUpFromLine className="h-5 w-5" />} tooltip={CUSTOM_RULES_NOTE} />
            <CountedStatCard label="Associated Subnets" total={totalAssociatedSubnets} of={nsgs.length} icon={<Globe2 className="h-5 w-5" />} />
          </div>

          <QuickGlance>
            <Card className="p-4 text-sm text-[var(--color-text-secondary)]">
              Showing the at-a-glance summary. Open this page on a larger screen for the full details.
            </Card>
          </QuickGlance>

          <Extended>
          {/* Filter bar (T034) */}
          <Card className="mb-6 p-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-12 md:items-end">
              <div className="md:col-span-5">
                <Input
                  isFullWidth
                  label="Search NSGs"
                  placeholder="Search by name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  leftAddon={<Search className="h-4 w-4" />}
                />
              </div>
              <div className="md:col-span-2">
                <Button variant="secondary" size="md" onClick={() => setSearch('')}>
                  Clear Filters
                </Button>
              </div>
            </div>
          </Card>

          {/* NSG table (T035) */}
          <Card padding="none">
            <div className="border-b border-[var(--color-border)] px-6 py-4">
              <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
                Network Security Groups
                <span className="ml-2 text-sm font-normal text-[var(--color-text-secondary)]">({nsgs.length} total)</span>
              </h2>
            </div>
            {nsgsQuery.isLoading ? (
              <div className="p-4">
                <LoadingState rows={6} />
              </div>
            ) : !nsgs.length ? (
              <EmptyState title="No network data" message="No network data collected yet." />
            ) : (
              <DataTable
                columns={columns}
                rows={nsgs}
                rowKey={(nsg) => nsg.id}
                minWidth="52rem"
                onRowClick={(nsg) => setSelectedNsgId(nsg.id)}
                emptyMessage="No network data collected yet."
              />
            )}
          </Card>
          </Extended>
        </>
      )}

      {/* NSG Detail Modal (T036) */}
      {selectedNsgId !== null && (
        <NsgDetailModal nsgId={selectedNsgId} onClose={() => setSelectedNsgId(null)} />
      )}
    </PageLayout>
  );
};

export default NsgSummaryPage;
